import {
  ActionIcon,
  Badge,
  Button,
  Flex,
  Paper,
  Space,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import React, { useState } from "react";
import { HallPartOption } from "../../../constants/types";
import { DeleteIcon, EditIcon, PlusIcon, XOctagonIcon } from "lucide-react";
import AddPillModal from "./AddPillModal";

interface Props {
  t: (key: string) => string;
  setSelectionSectorNameChange: React.Dispatch<
    React.SetStateAction<HallPartOption | null>
  >;
}

const ChangeSector: React.FC<Props> = ({ t, setSelectionSectorNameChange }) => {
  const storedHallPartsNames = localStorage.getItem("hallPartsNames");
  const initialOptions: HallPartOption[] = storedHallPartsNames
    ? JSON.parse(storedHallPartsNames)
    : [];

  const [options, setOptions] = useState<HallPartOption[]>(initialOptions);
  const [opened, setOpened] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPill, setCurrentPill] = useState<HallPartOption | null>(null);
  const [newPillLabel, setNewPillLabel] = useState<string>("");
  const [activePill, setActivePill] = useState<HallPartOption | null>(null);

  const handleAddPill = (newPill: string) => {
    const newPillOption: HallPartOption = {
      labelKey: newPill,
      value: newPill.toLowerCase().replace(/\s+/g, "_"),
    };
    const updatedOptions = [...options, newPillOption];
    setOptions(updatedOptions);
    localStorage.setItem("hallPartsNames", JSON.stringify(updatedOptions));
    setOpened(false);
  };

  const handleClick = (option: HallPartOption) => {
    setActivePill(option);
    setSelectionSectorNameChange(option);
  };

  const handleDelete = (option: HallPartOption) => {
    const updatedOptions = options.filter((opt) => opt !== option);
    setOptions(updatedOptions);
    localStorage.setItem("hallPartsNames", JSON.stringify(updatedOptions));
  };

  const handleEdit = (option: HallPartOption) => {
    setEditMode(true);
    setCurrentPill(option);
    setNewPillLabel(option.value);
    setOpened(true);
  };

  const handleSaveEdit = () => {
    if (currentPill && newPillLabel.trim()) {
      const updatedOptions = options.map((opt) =>
        opt === currentPill ? { ...opt, labelKey: newPillLabel } : opt
      );
      setOptions(updatedOptions);
      localStorage.setItem("hallPartsNames", JSON.stringify(updatedOptions));
      setOpened(false);
      setEditMode(false);
      setCurrentPill(null);
    }
  };

  const pills = options.map((option, index) => (
    <Flex
      w={"100%"}
      justify={"space-between"}
      onClick={() => handleClick(option)}
      align={"stretch"}
      key={`pill_${index}`}
    >
      <Badge
        style={{
          backgroundColor: activePill === option ? "#07714f" : "",
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxSizing: "border-box",
          borderBottomRightRadius: 0,
          borderTopRightRadius: 0,
          cursor: "pointer",
          textTransform: "lowercase",
        }}
        p={12}
        mb={10}
        variant={"filled"}
        color={"gray"}
      >
        <Text
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "5px",
          }}
        >
          {option.color && (
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: option.color,
              }}
            ></div>
          )}
          {t(option.labelKey)}
        </Text>
      </Badge>

      <Flex>
        <Button
          radius={0}
          color={"cyan"}
          size="compact-sm"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(option);
          }}
        >
          <EditIcon size={14} />
        </Button>

        <Button
          radius={0}
          color={"red"}
          size="compact-sm"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(option);
          }}
        >
          <DeleteIcon size={14} />
        </Button>
      </Flex>
    </Flex>
  ));

  return (
    <>
      <Flex w={"100%"} justify={"space-between"}>
        <Title order={4}>{t("common.actions")}</Title>

        <Flex gap={8}>
          <Tooltip label={t("common.clearChoice")}>
            <ActionIcon
              variant="filled"
              color="gray"
              onClick={() => {
                setActivePill(null);
                setSelectionSectorNameChange(null);
              }}
            >
              <XOctagonIcon size={16} />
            </ActionIcon>
          </Tooltip>

          <ActionIcon
            variant="filled"
            color="blue"
            onClick={() => {
              setOpened(true);
              setEditMode(false);
            }}
          >
            <PlusIcon size={16} />
          </ActionIcon>
        </Flex>
      </Flex>

      <Space h={"sm"} />
      {pills}

      <AddPillModal
        opened={opened}
        setOpened={setOpened}
        handleAddPill={handleAddPill}
        newPillLabel={newPillLabel}
        setNewPillLabel={setNewPillLabel}
        handleSaveEdit={handleSaveEdit}
        editMode={editMode}
        t={t}
      />
    </>
  );
};

export default React.memo(ChangeSector);
